import { Textarea } from '@components/ui/textarea';
import { cn } from '../../../lib/utils';
import { useFieldContext } from './form';
import { FormDescription, FormErrorMessage, FormField, FormLabel } from './shared.component';
import { restoreTextareaFocusAfterChange } from './text-area-field.util';

type TextAreaFieldProps = React.ComponentProps<typeof Textarea> & {
  className?: string;
  description?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  required?: boolean;
  textareaContainerClassName?: string;
  textareaClassName?: string;
  tooltip?: React.ReactNode;
};

/**
 * A wrapper component over the basic Textarea that adds TanStack Form logic and validation.
 * Provides form field context, error handling, accessibility features, and multi-line text input.
 * @param props - All other standard HTML textarea props plus custom styling options
 * @returns A form-integrated textarea field component for multi-line text input
 */
export function TextAreaField(props: TextAreaFieldProps) {
  const {
    className,
    description,
    label,
    labelSuffix,
    placeholder,
    required,
    rows = 3,
    textareaClassName,
    textareaContainerClassName,
    tooltip,
    ...otherProps
  } = props;
  const field = useFieldContext<string>();

  return (
    <FormField className={cn('w-full', className)}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          labelSuffix={labelSuffix}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}
      {description && <FormDescription>{description}</FormDescription>}
      <Textarea
        aria-invalid={!field.state.meta.isValid}
        className={textareaClassName}
        containerClassName={textareaContainerClassName}
        id={field.name}
        name={field.name}
        onBlur={field.handleBlur}
        hasError={!field.state.meta.isValid}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          field.handleChange(e.target.value);
          restoreTextareaFocusAfterChange({ fieldName: field.name });
        }}
        placeholder={placeholder}
        rows={rows}
        value={field.state.value ?? ''}
        {...otherProps}
      />
      <FormErrorMessage />
    </FormField>
  );
}
